import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { apiResetPassword, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Missing reset token. Request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters."); return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match."); return;
    }
    setBusy(true);
    try {
      await apiResetPassword({ token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="pt-16 pb-20 max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="border border-neutral-900 bg-neutral-950/60 p-7"
        data-testid="reset-password-page"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          ── set new password ──
        </div>
        <h1 className="text-3xl font-black tracking-tight text-white">Reset password</h1>

        {done ? (
          <div className="mt-6" data-testid="reset-success">
            <div className="flex items-center gap-2 text-sm text-[#00FF66] mb-3">
              <CheckCircle2 className="w-4 h-4" /> Password updated
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed">
              You can now sign in with your new password. Redirecting…
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-400 mt-1.5 mb-6">
              Choose a strong new password — minimum 6 characters.
            </p>
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <Field icon={<Lock className="w-4 h-4" />} placeholder="New password" type="password" value={password} onChange={setPassword} testid="reset-new-password" />
              <Field icon={<Lock className="w-4 h-4" />} placeholder="Confirm new password" type="password" value={confirm} onChange={setConfirm} testid="reset-confirm-password" />
              {err && (
                <div className="text-xs text-red-400 flex items-center gap-1.5" data-testid="reset-error">
                  <AlertCircle className="w-3.5 h-3.5" /> {err}
                </div>
              )}
              <button
                type="submit" disabled={busy || !token} data-testid="reset-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors disabled:opacity-50"
              >
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
            <div className="mt-5 text-xs text-neutral-500 text-center">
              <Link to="/login" data-testid="reset-back" className="text-neutral-400 hover:text-white">
                ← back to login
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </section>
  );
}

function Field({ icon, placeholder, type, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3.5 focus-within:border-neutral-600 transition-colors">
      <span className="text-neutral-500">{icon}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="flex-1 bg-transparent border-0 outline-none py-3 text-sm text-white placeholder:text-neutral-600"
        autoComplete="new-password"
      />
    </label>
  );
}
